/**
 * Company and Multi-Tenant Types
 * Phase 9: Multi-Tenant Company Management
 */

// =====================================================
// Enums
// =====================================================

export type CompanyRole = 'owner' | 'admin' | 'manager' | 'sales_rep';

export type InvitationStatus = 'pending' | 'accepted' | 'expired' | 'cancelled';

// =====================================================
// Settings
// =====================================================

import type { CustomFieldsSettings } from './customFields';

/**
 * Custom field options for dropdowns and picklists
 * @deprecated Use CustomFieldsSettings from customFields.ts instead
 */
export interface CustomFieldOptions {
  /** Custom organization type values (e.g., ['Merchant', 'Producer', 'Service']) */
  organizationTypes?: string[];
}

/**
 * Company Settings stored in the settings JSONB field
 */
export interface CompanySettings {
  /** Whether to display the company logo in the application header */
  showLogoInHeader?: boolean;
  /** Height of the logo in the header in pixels (default: 32, range: 24-80) */
  headerLogoHeight?: number;
  /** Default language for the company (used for AI, date formatting, new users) */
  defaultLanguage?: 'en-US' | 'de-DE' | 'fr-FR' | 'es-ES' | 'it-IT' | 'nl-NL';
  /**
   * Custom field options for various entity types
   * Supports both legacy format (CustomFieldOptions) and new format (CustomFieldsSettings)
   */
  customFields?: CustomFieldOptions & CustomFieldsSettings;
  /** Branding customization settings */
  branding?: {
    /** Dark mode specific branding overrides */
    darkMode?: {
      /** Primary UI color for dark mode (hex color code) - overrides primaryColor when dark mode is active */
      primaryColor?: string;
    };
  };
}

// =====================================================
// Database Tables
// =====================================================

/**
 * Company - CRM user companies (not customer organizations)
 */
export interface Company {
  id: string;
  name: string;
  website: string | null;
  logoUrl: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  brandColor: string; // Hex color code, default: #3B82F6
  primaryColor: string | null; // Primary UI theme color (overrides brandColor for UI)
  defaultCurrency: string; // ISO 4217 currency code, default: USD
  productMode: 'full' | 'feedback'; // Phase 159: decides accessible product shells
  address: string | null;
  city: string | null;
  postalCode: string | null;
  country: string | null;
  settings: CompanySettings; // JSONB - company preferences and settings
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

/**
 * Company Member - Join table linking users to companies with roles
 */
export interface CompanyMember {
  id: string;
  companyId: string;
  userId: string;
  role: CompanyRole;
  invitedBy: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

/**
 * Invitation - Pending team member invitations
 */
export interface Invitation {
  id: string;
  companyId: string;
  email: string;
  role: CompanyRole;
  token: string; // Secure random token for invitation link
  status: InvitationStatus;
  invitedBy: string;
  createdAt: string;
  acceptedAt: string | null;
  expiresAt: string; // Default: 7 days from creation
}

// =====================================================
// Extended Types with Relations
// =====================================================

/**
 * Company Member with User details
 */
export interface CompanyMemberWithUser extends CompanyMember {
  user: {
    id: string;
    email: string;
    fullName: string;
    avatarUrl: string | null;
  };
}

/**
 * Company Member with Company details
 */
export interface CompanyMemberWithCompany extends CompanyMember {
  company: Company;
}

/**
 * Invitation with Inviter details
 */
export interface InvitationWithInviter extends Invitation {
  inviter: {
    id: string;
    email: string;
    fullName: string;
  };
}

/**
 * Invitation with Company details
 */
export interface InvitationWithCompany extends Invitation {
  company: Company;
}

/**
 * Full Invitation details (with company and inviter)
 */
export interface InvitationFull extends Invitation {
  company: Company;
  inviter: {
    id: string;
    email: string;
    fullName: string;
  };
}

// =====================================================
// Request/Response Types
// =====================================================

/**
 * Create Company Request
 */
export interface CreateCompanyRequest {
  name: string;
  logoUrl?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  brandColor?: string; // Default: #3B82F6
  primaryColor?: string | null; // Default: null (falls back to brandColor)
  address?: string | null;
  city?: string | null;
  postalCode?: string | null;
  country?: string | null;
}

/**
 * Update Company Request
 */
export interface UpdateCompanyRequest {
  name?: string;
  logoUrl?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  website?: string | null;
  brandColor?: string;
  primaryColor?: string | null;
  defaultCurrency?: string;
  address?: string | null;
  city?: string | null;
  postalCode?: string | null;
  country?: string | null;
  settings?: Partial<CompanySettings>;
}

/**
 * Invite Member Request
 */
export interface InviteMemberRequest {
  email: string;
  role: CompanyRole;
  message?: string; // Optional personal message
}

/**
 * Update Member Role Request
 */
export interface UpdateMemberRoleRequest {
  memberId: string;
  role: CompanyRole;
}

/**
 * Accept Invitation Request
 */
export interface AcceptInvitationRequest {
  token: string;
  userId?: string; // Set if user already has account
}

// =====================================================
// Permission Helpers
// =====================================================

/**
 * Check if role has admin privileges (owner or admin)
 */
export function isAdmin(role: CompanyRole): boolean {
  return role === 'owner' || role === 'admin';
}

/**
 * Check if role can manage team (invite, remove members)
 */
export function canManageTeam(role: CompanyRole): boolean {
  return role === 'owner' || role === 'admin';
}

/**
 * Check if role can edit company settings
 */
export function canEditCompany(role: CompanyRole): boolean {
  return role === 'owner' || role === 'admin';
}

/**
 * Check if role can delete company
 */
export function canDeleteCompany(role: CompanyRole): boolean {
  return role === 'owner';
}

/**
 * Check if role can view all company deals
 */
export function canViewAllDeals(role: CompanyRole): boolean {
  return role === 'owner' || role === 'admin' || role === 'manager';
}

/**
 * Check if role can view all company tasks
 */
export function canViewAllTasks(role: CompanyRole): boolean {
  return role === 'owner' || role === 'admin' || role === 'manager';
}

/**
 * Check if role can update/delete all company tasks
 */
export function canManageAllTasks(role: CompanyRole): boolean {
  return role === 'owner' || role === 'admin' || role === 'manager';
}

/**
 * Check if role can manage task statuses (create/update/archive).
 * Only admin/owner by default — use per-user overrides to grant others access.
 */
export function canManageTaskStatuses(role: CompanyRole): boolean {
  return role === 'admin' || role === 'owner';
}

// =====================================================
// Enrichment Permissions
// =====================================================

/**
 * Check if role can trigger single enrichment
 * All authenticated users can trigger single enrichment
 */
export function canTriggerEnrichment(role: CompanyRole): boolean {
  return role === 'owner' || role === 'admin' || role === 'manager' || role === 'sales_rep';
}

/**
 * Check if role can trigger bulk enrichment
 * Requires manager+ role to prevent credit abuse
 */
export function canTriggerBulkEnrichment(role: CompanyRole): boolean {
  return role === 'owner' || role === 'admin' || role === 'manager';
}

/**
 * Check if role can configure enrichment settings
 * Includes: waterfall priorities, skip conditions, overwrite modes
 */
export function canConfigureEnrichment(role: CompanyRole): boolean {
  return role === 'owner' || role === 'admin' || role === 'manager';
}

/**
 * Check if role can set auto-enrich rules and budgets
 * Requires admin+ due to cost implications
 */
export function canSetEnrichmentBudget(role: CompanyRole): boolean {
  return role === 'owner' || role === 'admin';
}

/**
 * Get role display name
 */
export function getRoleDisplayName(role: CompanyRole): string {
  const roleNames: Record<CompanyRole, string> = {
    owner: 'Owner',
    admin: 'Admin',
    manager: 'Manager',
    sales_rep: 'Sales Rep',
  };
  return roleNames[role];
}

/**
 * Get role badge color (Tailwind classes)
 */
export function getRoleBadgeColor(role: CompanyRole): string {
  const colors: Record<CompanyRole, string> = {
    owner: 'bg-yellow-100 text-yellow-800',
    admin: 'bg-blue-100 text-blue-800',
    manager: 'bg-purple-100 text-purple-800',
    sales_rep: 'bg-gray-100 text-gray-800',
  };
  return colors[role];
}

/**
 * Check if invitation is expired
 */
export function isInvitationExpired(invitation: Invitation): boolean {
  return new Date(invitation.expiresAt) < new Date();
}

/**
 * Get time until invitation expires
 */
export function getInvitationTimeRemaining(invitation: Invitation): string {
  const now = new Date();
  const expires = new Date(invitation.expiresAt);
  const diffMs = expires.getTime() - now.getTime();

  if (diffMs <= 0) {
    return 'Expired';
  }

  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 0) {
    return `${diffDays} day${diffDays > 1 ? 's' : ''} left`;
  }

  if (diffHours > 0) {
    return `${diffHours} hour${diffHours > 1 ? 's' : ''} left`;
  }

  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} left`;
}

/**
 * Check if invitation is expiring soon (< 24 hours)
 */
export function isInvitationExpiringSoon(invitation: Invitation): boolean {
  const now = new Date();
  const expires = new Date(invitation.expiresAt);
  const diffMs = expires.getTime() - now.getTime();
  const diffHours = diffMs / (1000 * 60 * 60);

  return diffHours > 0 && diffHours < 24;
}
